import { AfterViewInit, Component, Injector, Input, OnInit } from '@angular/core';
import { TwoColumnLayoutComponent } from '../two-column-layout/two-column-layout.component';
import { WidgetFactoryService } from '../../services/widget-factory.service';
import { trans } from '../../../localization';
import { WidgetListModal } from '../../modals/widget-list-modal/widget-list.modal';
import { ToolbarService } from '../../../layouts/toolbar/services/toolbar.service';
import { ModalService } from '../../../modals';
import { DashboardService } from '../../services/dashboard.service';
import { DashboardModel } from '../../models/dashboard.model';
import { WidgetModel } from '../../models/widget.model';
import { ActiveDashboard } from '../../classes/active-dashboard';
import { AddWidgetModal } from '../../modals/add-widget-modal/add-widget.modal';
import { ALERT_MESSAGES } from '../../../alerts/alert-messages';
import { AlertService } from '../../../alerts';
import { ToastService } from '../../../toast/services/toast.service';
import { WidgetService } from '../../services/widget.service';
import { Subject } from 'rxjs/Subject';
import { ReplaySubject } from 'rxjs/ReplaySubject';
import { clone } from '../../../support/helpers/clone.function';
import { ApiService } from '../../../api/api.service';
import { BUTTONS } from '../../../support';

@Component({
    selector: 'chassis-core-dashboard',
    templateUrl: './dashboard.component.html',
    styleUrls: ['./dashboard.component.scss'],
    providers: [WidgetService]
})
export class DashboardComponent implements OnInit, AfterViewInit {

    /**
     * The layout to use.
     *
     * @type {TwoColumnLayoutComponent}
     */
    @Input() public layout = TwoColumnLayoutComponent;

    /**
     * The layout's widget containers.
     *
     * Do not break the object pointer! If broken the child components
     * can not mutate the original instance and features won't work.
     *
     * @type {any[]}
     */
    @Input() public containers: Array<Array<WidgetModel>> = [];

    /**
     * A copy of the previous value of the container instance, which is
     * used to check for changes.
     */
    public previousValue: Array<Array<WidgetModel>>;

    public dashboard: Subject<DashboardModel> = new ReplaySubject(1);

    /**
     * A custom service.
     */
    @Input() public service: ApiService;

    constructor(private toolbarService: ToolbarService, private modalService: ModalService,
                private alertService: AlertService, private widgetFactoryService: WidgetFactoryService,
                private dashboardService: DashboardService, private toastService: ToastService,
                private widgetService: WidgetService) {
    }

    ngOnInit() {
        this.service = this.service ? this.service : this.dashboardService;

        this.setToolbarButtons();
    }

    ngAfterViewInit() {
        // Load data after view initialization so containers
        // have the ability to fill empty or non existing
        // containers
        this.loadData();
    }

    /**
     * Load the dashboard data.
     */
    private loadData() {
        const request = this.service.request('get',
            this.service.getEndpointUrl(), false);

        request.subscribe((data: DashboardModel) => {
            this.dashboard.next(data);

            this.setWidgetData(data.widgets);
        });
    }

    /**
     * Set the merged widget data of the given containers.
     *
     * @param {{[p: number]: Array<any>}} containers
     */
    private setWidgetData(containers: { [key: number]: Array<any> }) {
        Object.keys(containers).forEach((index) => {
            const widgets = containers[index].map((row: any) => {
                return this.getMergedWidget(row);
            }).filter((item) => {
                return !!item;
            });

            widgets.forEach((widget) => {
                this.containers[index].push(widget);
            });
        });

        this.setPreviousValue();
    }

    /**
     * Merge and return the widget with the registered data
     * and the saved data.
     *
     * @param {WidgetModel} widget
     * @return {{} & WidgetInterface & WidgetModel}
     */
    private getMergedWidget(widget: WidgetModel) {
        let registered = this.widgetFactoryService.get(widget.name);

        // Remove object pointer
        registered = clone(registered);

        const merged = Object.assign(registered, widget);

        // Create and return a new instance
        return registered ? merged : null;
    }

    private setToolbarButtons() {
        this.addAddWidgetButton();
    }

    private addAddWidgetButton() {
        this.toolbarService.add({
            name: BUTTONS.ADD.NAME,
            text: trans(BUTTONS.ADD.TEXT),
            icon: BUTTONS.ADD.ICON,
            callback: () => {
                this.addWidget();
            },
        });
    }

    private addWidget() {
        this.dashboard.subscribe((dashboard) => {
            const modal = this.modalService.open(WidgetListModal);

            modal.closing((resolve, reject, result) => {
                result['order'] = this.containers.length ?
                    this.containers[0].length + 1 : 1;

                const request = this.widgetService.post(result, {
                    dashboardId: dashboard.id
                });

                request.subscribe((data) => {
                    result = this.getMergedWidget(data);

                    this.toastService.success('created', trans('Widget added to dashboard'));

                    resolve(result);
                });
            });

            modal.closed((result) => {
                this.containers[0].push(result);
            });
        });
    }

    /**
     * Create an injector with the active dashboard reference for
     * the containers and widgets.
     *
     * @return {Injector}
     */
    public createInjector() {
        const dashboard = new ActiveDashboard();

        // Add save callback
        dashboard.save = () => {
            this.save();
        };

        // Add dashboard add widget callback
        dashboard.addWidget = () => {
            this.addWidget();
        };

        // Add dashboard edit widget callback
        dashboard.editWidget = (data) => {
            this.editWidget(data);
        };

        // Add dashboard remove widget callback
        dashboard.removeWidget = (data) => {
            this.removeWidget(data);
        };

        return Injector.create({
            providers: [{
                provide: ActiveDashboard,
                useValue: dashboard
            }],
        });
    }

    private editWidget(data) {
        const modal = this.modalService.open(AddWidgetModal, {
            widget: data.widget,
        });
    }

    private removeWidget(data) {
        this.dashboard.subscribe((dashboard) => {
            const alert = this.alertService.open({
                message: ALERT_MESSAGES.ARE_YOU_SURE,
            });

            alert.closing((resolve, reject) => {
                const request = this.widgetService.delete(data.widget.id, {
                    dashboardId: dashboard.id
                });

                request.subscribe((result) => {
                    this.toastService.success('deleted', trans('Widget removed from dashboard'));

                    resolve(result);
                }, (error) => {
                    reject(error);
                });
            });

            alert.closed(() => {
                const container = data.container;

                this.containers[container].splice(this.containers[container].findIndex((i) => {
                    return i.id === data.widget.id;
                }), 1);
            });
        });
    }

    public save() {
        if (this.hasChanged()) {
            this.setPreviousValue();

            const data = this.containers.map((container) => {
                return container.map((widget) => {
                    return {
                        id: widget.id,
                        name: widget.name
                    };
                });
            });

            const request = this.service.request('post',
                this.service.getEndpointUrl(), false, {
                    containers: data
                });

            request.subscribe(() => {
                this.toastService.success('saved');
            });
        }
    }

    /**
     * Set a copy of the current container values in order to handle change detection.
     */
    private setPreviousValue() {
        this.previousValue = Array.from(this.containers).map((container) => {
            return Array.from(container);
        }) || [[]];
    }

    /**
     * Check if the dashboard containers have changed or the widget
     * orders have changed.
     *
     * @return {boolean}
     */
    public hasChanged(): boolean {
        // Loop through all containers
        for (const containerIndex in this.containers) {
            if (this.containers.hasOwnProperty(containerIndex)) {
                const container         = this.containers[containerIndex];
                const containerPrevious = this.previousValue[containerIndex];

                // Check if the container lengths differ, if so
                // value has changed
                if (container.length !== containerPrevious.length) {
                    return true;
                }

                if (this.haveWidgetOrdersChanged(container, containerPrevious)) {
                    return true;
                }
            }
        }

        return false;
    }

    private haveWidgetOrdersChanged(container, containerPrevious) {
        // Loop through all widgets in the container
        for (const widgetIndex in container) {
            if (container.hasOwnProperty(widgetIndex)) {
                const widget         = container[widgetIndex];
                const widgetPrevious = containerPrevious[widgetIndex];

                // Check if all widgets are on the same position as
                // before, if not the value has changed
                if (widget !== widgetPrevious) {
                    return true;
                }
            }
        }

        return false;
    }

}



// WEBPACK FOOTER //
// ./libs/chassis-core/src/app/dashboards/components/dashboard/dashboard.component.ts